import { useEffect, useRef, useState } from "react";
import {
  Combobox,
  ComboboxInput,
  ComboboxPopover,
  ComboboxList,
  ComboboxOption,
} from "@reach/combobox";
import "@reach/combobox/styles.css";
import usePlacesAutocomplete, {
  getGeocode,
  getLatLng,
} from "use-places-autocomplete";
import { validateSpaceAddressFields } from "./validateSpaceAddressFields";
import globalService from "../../../redux/services/global.service";
// Custom debounce function
const customDebounce = (func, delay) => {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    return new Promise((resolve) => {
      timeoutId = setTimeout(async () => {
        const result = await func(...args);
        resolve(result);
      }, delay);
    });
  };
};

// Custom debounce function
const customDebounceErrorHandling = (func, delay) => {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    return new Promise(async (resolve) => {
      timeoutId = setTimeout(async () => {
        try {
          const result = await func(...args);
          resolve(result);
        } catch (error) {
          resolve(null); // Handle errors and return null
        }
      }, delay);
    });
  };
};

const getAddressOne = (address) => {
  if (!address || address.length <= 0) {
    return "";
  }

  const addressComponents = address[0].address_components;
  const streetNumber = addressComponents.find(
    (ac) => ac.types[0] === "street_number"
  );
  const routeValue = addressComponents.find((ac) => ac.types[0] === "route");

  return streetNumber
    ? `${streetNumber?.long_name + " " + routeValue?.long_name}`
    : routeValue?.long_name;
};

const getStateCode = (address) => {
  if (!address || address.length <= 0) {
    return "";
  }

  const addressComponents = address[0].address_components;
  const stateValue = addressComponents.find(
    (ac) => ac.types[0] === "administrative_area_level_1"
  );

  return stateValue?.short_name || "";
};
const getCity = (address) => {
  if (!address || address.length <= 0) {
    return "";
  }

  const addressComponents = address[0].address_components;
  const cityValue = addressComponents.find((ac) => ac?.types[0] === "locality");
  return cityValue?.long_name || "";
};
const getZipCode = (address) => {
  if (!address || address.length <= 0) {
    return "";
  }

  const addressComponents = address[0].address_components;
  const zipValue = addressComponents.find(
    (ac) => ac.types[0] === "postal_code"
  );
  return zipValue?.long_name || "";
};

export default function SpaceAddress({
  formData,
  setFormData,
  setUspsError,
  uspsError,
  addressErrors,
  setAddressErrors,
  addressInvalidError,
  setAddressInvalidError,
  isDisabledEverything,
  isPrepopulated,
  setIsPrepopulated,
}) {
  const {
    ready,
    value,
    setValue,
    suggestions: { status, data },
    clearSuggestions,
  } = usePlacesAutocomplete({
    requestOptions: {
      componentRestrictions: { country: ["US"] },
    },
  });

  const [cities, setCities] = useState([]);
  const [countryList, setCountryList] = useState([]);
  const [stateList, setStateList] = useState([]);
  const [isSameAsProfileAddress, setIsSameAsProfileAddress] = useState(false);
  const [warningForSameAsProfile, setWarningForSameAsProfile] = useState(false);

  const getCoordinates = async (address) => {
    // addressOne validation is handling by field level validation.

    if (address !== "") {
      try {
        const geocodeResults = await getGeocode({ address });
        if (geocodeResults.length === 0) {
          throw new Error("No geocode results found.");
        }
        const latLng = await getLatLng(geocodeResults[0]);
        return latLng;
      } catch (error) {
        return null;
      }
    }
  };

  const debounceGetCoordinates = useRef(
    customDebounceErrorHandling(getCoordinates, 500)
  );

  // const debounceGetVerifyAddress = useRef(
  //   customDebounce(globalService.getVerifyAddress, 500)
  // );

  const fetchAllCountries = async () => {
    const result = await globalService.getAllCountries();
    if (result?.data?.listCountries) {
      setCountryList(result.data.listCountries);
    } else {
      console.log("Unable to fetch country list");
    }
  };

  const fetchAllStates = async (selectedCountryId) => {
    const result = await globalService.getAllStates(selectedCountryId);
    if (result?.data?.listStates) {
      setStateList(result.data.listStates);
    } else {
      console.log("Unable to fetch State list");
    }
  };

  const getCities = async () => {
    const { data } = await globalService.getAllCities();
    if (data?.listCities?.rows && data?.listCities?.rows?.length) {
      setCities(data?.listCities?.rows);
    } else {
      setCities([]);
    }
  };

  useEffect(() => {
    fetchAllStates(formData?.countryId);
  }, [countryList]);

  useEffect(() => {
    // fetch country list API
    fetchAllCountries();
    getCities();
  }, []);

  // this will be used to all form field checks that requires api.
  const debounceChecks = async (name, value) => {
    if (name === "zipCode") {
      // const result = await debounceGetVerifyAddress.current(value);

      // if (result?.data?.verifyAddress?.zipCode?.error?.description) {
      //   setUspsError(
      //     result?.data?.verifyAddress?.zipCode?.error?.description
      //   );
      // } else {
      //   const { zip5, city, state } =
      //     result?.data?.verifyAddress?.zipCode ?? {};

      //   if (
      //     formData?.stateCode?.toLowerCase()?.trim() === state?.toLowerCase() &&
      //     formData?.city?.toLowerCase()?.trim() === city?.toLowerCase() &&
      //     value?.trim() === zip5
      //   ) {
      //     setUspsError("");
      //   } else {
      //     setUspsError("Invalid city, state or zipCode");
      //   }
      // }

      // set all over address error.
      const latLng = await debounceGetCoordinates.current(
        `${formData.addressOne} ${formData.city ? formData.city : ""} ${
          formData.stateCode ? formData.stateCode : ""
        } ${value ? value : ""}`
      );
      if (latLng) {
        setFormData((prev) => ({
          ...prev,
          latitude: latLng.lat,
          longitude: latLng.lng,
        }));
        setAddressInvalidError("");
      } else {
        setAddressInvalidError("Please provide a valid address");
      }
    } else if (name === "city") {
      // city zip verification check.
      // const result = await debounceGetVerifyAddress.current(formData.zipCode);

      // if (result?.data?.verifyAddress?.zipCode?.error?.description) {
      //   setUspsError(
      //     result?.data?.verifyAddress?.zipCode?.error?.description
      //   );
      // } else {
      //   const { zip5, city, state } =
      //     result?.data?.verifyAddress?.zipCode ?? {};
      //   if (
      //     formData?.stateCode?.trim()?.toLowerCase() === state?.toLowerCase() &&
      //     value?.trim()?.toLowerCase() === city?.toLowerCase() &&
      //     formData?.trim()?.zipCode === zip5
      //   ) {
      //     setUspsError("");
      //   } else {
      //     setUspsError("Invalid city, state or zipCode");
      //   }
      // }

      // set all over address error.
      const latLng = await debounceGetCoordinates.current(
        `${formData.addressOne} ${value ? value : ""} ${
          formData.stateCode ? formData.stateCode : ""
        } ${formData.zipCode ? formData.zipCode : ""}`
      );
      if (latLng) {
        setFormData((prev) => ({
          ...prev,
          latitude: latLng.lat,
          longitude: latLng.lng,
        }));
        setAddressInvalidError("");
      } else {
        setAddressInvalidError("Please provide a valid address");
      }
    } else if (name === "stateCode") {
      // const result = await debounceGetVerifyAddress.current(formData.zipCode);

      // if (result?.data?.verifyAddress?.zipCode?.error?.description) {
      //   setUspsError(
      //     result?.data?.verifyAddress?.zipCode?.error?.description
      //   );
      // } else {
      //   const { zip5, city, state } =
      //     result?.data?.verifyAddress?.zipCode;
      //   if (
      //     value?.toLowerCase() === state?.toLowerCase() &&
      //     formData?.city?.toLowerCase() === city?.toLowerCase() &&
      //     formData?.zipCode === zip5
      //   ) {
      //     setUspsError("");
      //   } else {
      //     setUspsError("Invalid city, state or zipCode");
      //   }
      // }

      // set all over address error.
      const latLng = await debounceGetCoordinates.current(
        `${formData.addressOne} ${formData.city ? formData.city : ""} ${
          value ? value : ""
        } ${formData.zipCode ? formData.zipCode : ""}`
      );

      if (latLng) {
        setFormData((prev) => ({
          ...prev,
          latitude: latLng.lat,
          longitude: latLng.lng,
        }));
        setAddressInvalidError("");
      } else {
        setAddressInvalidError("Please provide a valid address");
      }
    } else if (name === "addressOne") {
      // set all over address error.
      const latLng = await debounceGetCoordinates.current(
        `${value} ${formData.city ? formData.city : ""} ${
          formData.stateCode ? formData.stateCode : ""
        } ${formData.zipCode ? formData.zipCode : ""}`
      );
      if (latLng) {
        setFormData((prev) => ({
          ...prev,
          latitude: latLng.lat,
          longitude: latLng.lng,
        }));
        setAddressInvalidError("");
      } else {
        setAddressInvalidError("Please provide a valid address");
      }
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Clear email and name availability errors when their respective fields change
    if (name === "zipCode") {
      setUspsError("");
    }
    // if (name === 'city') {
    //     setCityUnavailableError('');
    // }

    // Validate the field onChange
    const fieldErrors = validateSpaceAddressFields(name, value);

    // Update the form data and errors
    setFormData((prev) => ({ ...prev, [name]: value }));
    setAddressErrors((prev) => ({ ...prev, [name]: fieldErrors }));

    // Trigger email and name availability checks after debouncing
    debounceChecks(name, value);
  };

  const handleAutocompleteChange = (e) => {
    setIsPrepopulated(false);
    const { name, value } = e.target;

    if (name === "addressOne") {
      setAddressInvalidError("");
    }
    setValue(value);

    // Validate the field onChange
    const fieldErrors = validateSpaceAddressFields(name, value);

    // what ever user is typing set as addressOne
    setFormData((prev) => ({ ...prev, [name]: value }));
    setAddressErrors((prev) => ({ ...prev, [name]: fieldErrors }));

    debounceChecks(name, value);
  };

  // Event handler for address selection
  const handleSelect = async (address) => {
    const results = await getGeocode({ address });
    const addressOne = getAddressOne(results);
    const city = getCity(results);
    const stateCode = getStateCode(results);
    const zipCode = getZipCode(results);

    const selectedObj = {
      addressOne,
      city,
      stateCode,
      zipCode,
    };

    const coordinates = await getCoordinates(address);

    // take this address, and fetch streetaddress, city, state, zipCode and prefill.
    setFormData((prev) => ({
      ...prev,
      addressOne,
      city,
      zipCode,
      stateCode,
      latitude: coordinates.lat,
      longitude: coordinates.lng,
    }));

    // setSelectedAddress(address);
    setValue(addressOne, false);
    setIsPrepopulated(true);

    // on select from dropdown address is always valid wrt lat lng
    // setAddressInvalidError('');
    clearSuggestions();

    // check apis
    // Check USPS address check
    // const result = await globalService.getVerifyAddress(zipCode);
    // if (result?.data?.verifyAddress?.zipCode?.error?.description) {
    //   setUspsError(
    //     result?.data?.verifyAddress?.zipCode?.error?.description
    //   );
    //   // allErrors.zipCode = [`${result?.data?.verifyAddress?.zipCode?.error?.description}`];
    // } else {
    //   const { zip5, city, state } = result?.data?.verifyAddress?.zipCode;
    //   if (
    //     stateCode?.toLowerCase()?.trim() === state?.toLowerCase() &&
    //     city?.toLowerCase()?.trim() === city?.toLowerCase() &&
    //     zipCode?.trim() === zip5
    //   ) {
    //     setUspsError("");
    //     // allErrors.zipCode = ['zipCode doesnot belong to following state and city'];
    //   } else {
    //     setUspsError("Invalid city, state or zipCode");
    //     // allErrors.zipCode = ['zipCode doesnot belong to following state and city'];
    //   }
    // }

    // valid place with coordinate check
    if (addressOne !== "") {
      const latLng = await getCoordinates(
        `${addressOne} ${city ? city : ""} ${stateCode ? stateCode : ""} ${
          zipCode ? zipCode : ""
        }`
      );
      if (latLng) {
        setFormData((prev) => ({
          ...prev,
          latitude: latLng.lat,
          longitude: latLng.lng,
        }));
        setAddressInvalidError("");
      } else {
        setAddressInvalidError("Please provide a valid address");
      }
    }

    // check individual fields
    for (const key in selectedObj) {
      const value = selectedObj[key];
      const fieldErrors = validateSpaceAddressFields(key, value);
      setAddressErrors((prev) => ({ ...prev, [key]: fieldErrors }));
    }
  };

  // Set the initial value of the autocomplete input from API data
  useEffect(() => {
    if (formData) {
      // this address will be set on parent component on Load.
      const { addressOne } = formData;
      setValue(addressOne);
    }
  }, [formData, setValue]);

  return (
    <div className="mt-2 mb-4 taxInfo-form">
      <div className="row g-3">
        <div className="col-12 c-form-field require">
          <label htmlFor="addressOne" className="form-label">
            Street Address
          </label>

          <Combobox onSelect={handleSelect}>
            <ComboboxInput
              id="addressOne"
              // eslint-disable-next-line no-unneeded-ternary
              value={value}
              onChange={handleAutocompleteChange}
              disabled={!ready}
              className="form-control form-control-custom"
              placeholder="Enter Street Address"
              name="addressOne"
              autoComplete="off"
            />
            {!isPrepopulated && data && data.length > 0 && (
              <ComboboxPopover style={{ zIndex: 10000 }}>
                <ComboboxList>
                  {status === "OK" &&
                    data.map(({ place_id, description }) => (
                      <ComboboxOption key={place_id} value={description} />
                    ))}
                </ComboboxList>
              </ComboboxPopover>
            )}
          </Combobox>

          {addressErrors.addressOne && (
            <span className="error d-block">
              {addressErrors.addressOne.join(", ")}
            </span>
          )}
          {addressInvalidError && (
            <span className="error d-block">{addressInvalidError}</span>
          )}
        </div>
        <div className="col-12 c-form-field">
          <label htmlFor="city" className="form-label">
            Apt Number
          </label>

          <input
            value={formData.addressTwo}
            type="text"
            className="form-control form-control-custom"
            id="addressTwo"
            placeholder="Enter Apt/House Number"
            name="addressTwo"
            onChange={handleChange}
            disabled={isDisabledEverything}
          />
        </div>
        <div className="col-12 col-md-6 c-form-field require">
          <label htmlFor="countryId" className="form-label">
            Country
          </label>
          {countryList && countryList.length > 0 && (
            <select
              name="countryId"
              id="countryId"
              className="form-select form-control-custom form-select"
              defaultValue={formData.countryId}
              onChange={handleChange}
              disabled={true}
            >
              <option value="">Select Country</option>
              {(!countryList || countryList.length === 0) && (
                <option>No options to select from</option>
              )}
              {countryList &&
                countryList?.map((option) => (
                  <option
                    key={option.id}
                    value={option.id}
                    selected={formData.countryId === option.id}
                  >
                    {option["nicename"]}
                  </option>
                ))}
            </select>
          )}
        </div>
        <div className="col-12 col-md-6 c-form-field require">
          <label htmlFor="stateCode" className="form-label">
            State
          </label>
          <select
            name="stateCode"
            id="stateCode"
            className="form-select form-control-custom form-select"
            defaultValue={formData?.stateCode}
            onChange={handleChange}
            disabled={isDisabledEverything}
          >
            <option value="">Select State</option>
            {(!stateList || stateList.length === 0) && (
              <option>No options to select from</option>
            )}
            {stateList &&
              stateList?.map((option) => (
                <option
                  key={option.id}
                  value={option?.code}
                  selected={formData?.stateCode === option.code}
                >
                  {option["name"]}
                </option>
              ))}
          </select>
          {addressErrors.stateCode && (
            <span className="error d-block">
              {addressErrors.stateCode.join(", ")}
            </span>
          )}
        </div>
        <div className="col-12 col-md-6 c-form-field require">
          <label htmlFor="city" className="form-label">
            City
          </label>

          <input
            value={formData.city}
            type="text"
            className="form-control form-control-custom"
            id="city"
            placeholder="Enter City"
            name="city"
            onChange={handleChange}
            disabled={isDisabledEverything}
          />
          {addressErrors.city && (
            <span className="error d-block">{addressErrors.city.join(", ")}</span>
          )}
        </div>
        <div className="col-12 col-md-6 c-form-field require">
          <label htmlFor="zipCode" className="form-label">
            Zip Code
          </label>

          <input
            value={formData.zipCode}
            type="text"
            className="form-control form-control-custom"
            id="zipCode"
            placeholder="Enter Zip code"
            name="zipCode"
            onChange={handleChange}
            disabled={isDisabledEverything}
          />
          {addressErrors.zipCode && (
            <span className="error d-block">
              {addressErrors.zipCode.join(", ")}
            </span>
          )}
          {formData.zipCode !== "" &&
            uspsError &&
            uspsError !== addressErrors.zipCode && (
              <span className="error d-block">{uspsError}</span>
            )}
        </div>
        <div className="col-12 mt-1">
          <span className="messageInfo">
            {`This address won't be shared with the Renter until the space reservation begins`}
          </span>
        </div>
      </div>
    </div>
  );
}
